/**
 * Rich Text Editor Component
 * 
 * A WYSIWYG editor built on React Quill with Material UI styling.
 * Features:
 * - Customizable toolbar with formatting options
 * - HTML content support with XSS sanitization
 * - Formik integration support
 * - Character count display
 * - Error state handling
 * - Responsive design
 * 
 * @usage
 * <RichTextEditor
 *   value={value}
 *   onChange={handleChange}
 *   placeholder="Enter content..."
 *   error={Boolean(error)}
 *   helperText={error}
 * />
 */

import React, { useRef, useMemo } from 'react';
import ReactQuill from 'react-quill';
import DOMPurify from 'dompurify';
import {
  Box,
  Typography,
  FormHelperText,
  useTheme,
} from '@mui/material';
import 'react-quill/dist/quill.snow.css';

// Sanitization configuration - only allow safe HTML tags
const SANITIZE_CONFIG = {
  ALLOWED_TAGS: [
    'p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'strike',
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'ul', 'ol', 'li',
    'a', 'blockquote', 'pre', 'code',
    'span', 'div',
  ],
  ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'style'],
  ALLOW_DATA_ATTR: false,
};

// Default toolbar configuration
const DEFAULT_TOOLBAR = [
  [{ header: [1, 2, 3, false] }],
  ['bold', 'italic', 'underline', 'strike'],
  [{ list: 'ordered' }, { list: 'bullet' }],
  ['blockquote'],
  ['clean'],
];

// Minimal toolbar for compact editors
const MINIMAL_TOOLBAR = [
  ['bold', 'italic', 'underline'],
  [{ list: 'ordered' }, { list: 'bullet' }],
  ['clean'],
];

/**
 * Sanitize HTML content to prevent XSS attacks
 * @param {string} html - Raw HTML content
 * @returns {string} Sanitized HTML
 */
const sanitizeHtml = (html) => {
  if (!html) return '';
  return DOMPurify.sanitize(html, SANITIZE_CONFIG);
};

/**
 * Strip HTML tags and get plain text (for character counting)
 * @param {string} html - HTML content
 * @returns {string} Plain text
 */
const stripHtml = (html) => {
  if (!html) return '';
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
};

/**
 * RichTextEditor Component
 * 
 * @param {string} lineHeight - 'compact' (1.4), 'normal' (1.6), or 'relaxed' (1.8)
 */
const RichTextEditor = ({
  value = '',
  onChange,
  onBlur,
  placeholder = 'Enter content...',
  label,
  required = false,
  error = false,
  helperText = '',
  minHeight = 200,
  maxHeight,
  maxLength,
  showCharCount = false,
  toolbar = 'default', // 'default', 'minimal', or custom array
  lineHeight = 'compact', // 'compact', 'normal', 'relaxed'
  readOnly = false,
  disabled = false,
  name,
  id,
  sx = {},
}) => {
  const theme = useTheme();
  const quillRef = useRef(null);

  // Line height values
  const lineHeightValues = {
    compact: 1.4,
    normal: 1.6,
    relaxed: 1.8,
  };
  const editorLineHeight = lineHeightValues[lineHeight] || 1.4;

  // Determine toolbar configuration
  const toolbarConfig = useMemo(() => {
    if (toolbar === 'minimal') return MINIMAL_TOOLBAR;
    if (toolbar === 'default') return DEFAULT_TOOLBAR;
    if (Array.isArray(toolbar)) return toolbar;
    return DEFAULT_TOOLBAR;
  }, [toolbar]);

  // Quill modules configuration
  const modules = useMemo(() => ({
    toolbar: readOnly || disabled ? false : toolbarConfig,
    clipboard: {
      matchVisual: false,
    },
  }), [toolbarConfig, readOnly, disabled]);

  // Quill formats
  const formats = useMemo(() => [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet',
    'blockquote'
  ], []);

  // Handle content change
  const handleChange = (content, delta, source, editor) => {
    // Get the HTML content
    let htmlContent = content;
    
    // Check if content is empty (Quill adds <p><br></p> for empty content)
    if (content === '<p><br></p>' || content === '<p></p>') {
      htmlContent = '';
    }
    
    // Sanitize the content
    const sanitizedContent = sanitizeHtml(htmlContent);
    
    if (onChange) {
      onChange(sanitizedContent);
    }
  };

  // Handle blur event
  const handleBlur = () => {
    if (onBlur) {
      onBlur({ target: { name, id } });
    }
  };

  // Calculate character count from plain text
  const plainText = stripHtml(value);
  const charCount = plainText.length;
  const isOverLimit = maxLength && charCount > maxLength;

  // Custom styles for Material UI integration
  const editorStyles = {
    '& .ql-container': {
      minHeight: minHeight,
      maxHeight: maxHeight || 'auto',
      overflow: maxHeight ? 'auto' : 'visible',
      fontSize: '1rem',
      fontFamily: theme.typography.fontFamily,
      borderColor: error ? theme.palette.error.main : theme.palette.divider,
      borderBottomLeftRadius: theme.shape.borderRadius,
      borderBottomRightRadius: theme.shape.borderRadius,
      backgroundColor: disabled ? theme.palette.action.disabledBackground : 'transparent',
    },
    '& .ql-toolbar': {
      borderColor: error ? theme.palette.error.main : theme.palette.divider,
      borderTopLeftRadius: theme.shape.borderRadius,
      borderTopRightRadius: theme.shape.borderRadius,
      backgroundColor: theme.palette.mode === 'dark' 
        ? theme.palette.grey[800] 
        : theme.palette.grey[50],
    },
    '& .ql-toolbar.ql-snow .ql-picker-label': {
      color: theme.palette.text.primary,
    },
    '& .ql-toolbar.ql-snow .ql-stroke': {
      stroke: theme.palette.text.secondary,
    },
    '& .ql-toolbar.ql-snow .ql-fill': {
      fill: theme.palette.text.secondary,
    },
    '& .ql-toolbar.ql-snow button:hover .ql-stroke': {
      stroke: theme.palette.primary.main,
    },
    '& .ql-toolbar.ql-snow button:hover .ql-fill': {
      fill: theme.palette.primary.main,
    },
    '& .ql-toolbar.ql-snow button.ql-active .ql-stroke': {
      stroke: theme.palette.primary.main,
    },
    '& .ql-toolbar.ql-snow button.ql-active .ql-fill': {
      fill: theme.palette.primary.main,
    },
    '& .ql-editor': {
      minHeight: minHeight - 42, // Account for toolbar height
      color: theme.palette.text.primary,
      lineHeight: editorLineHeight,
      '&.ql-blank::before': {
        color: theme.palette.text.disabled,
        fontStyle: 'normal',
      },
      // Compact paragraph styling - reduce default spacing
      '& p': {
        margin: 0,
        marginBottom: '0.25em',
        '&:last-child': {
          marginBottom: 0,
        },
      },
      // Compact list styling
      '& ul, & ol': {
        margin: '0.25em 0',
        paddingLeft: '1.5em',
        '& li': {
          marginBottom: '0.15em',
          paddingLeft: '0.25em',
        },
      },
      // Compact blockquote
      '& blockquote': {
        margin: '0.5em 0',
        paddingLeft: '1em',
        borderLeft: `3px solid ${theme.palette.grey[300]}`,
      },
      // Compact headers
      '& h1, & h2, & h3': {
        marginTop: '0.5em',
        marginBottom: '0.25em',
        '&:first-child': {
          marginTop: 0,
        },
      },
    },
    '& .ql-editor a': {
      color: theme.palette.primary.main,
    },
    '& .ql-snow .ql-picker.ql-header .ql-picker-label::before, & .ql-snow .ql-picker.ql-header .ql-picker-item::before': {
      content: '"Normal"',
    },
    '& .ql-snow .ql-picker.ql-header .ql-picker-label[data-value="1"]::before, & .ql-snow .ql-picker.ql-header .ql-picker-item[data-value="1"]::before': {
      content: '"Heading 1"',
    },
    '& .ql-snow .ql-picker.ql-header .ql-picker-label[data-value="2"]::before, & .ql-snow .ql-picker.ql-header .ql-picker-item[data-value="2"]::before': {
      content: '"Heading 2"',
    },
    '& .ql-snow .ql-picker.ql-header .ql-picker-label[data-value="3"]::before, & .ql-snow .ql-picker.ql-header .ql-picker-item[data-value="3"]::before': {
      content: '"Heading 3"',
    },
    // Focus state
    '&:focus-within .ql-toolbar': {
      borderColor: error ? theme.palette.error.main : theme.palette.primary.main,
    },
    '&:focus-within .ql-container': {
      borderColor: error ? theme.palette.error.main : theme.palette.primary.main,
    },
    ...sx,
  };

  return (
    <Box>
      {/* Label */}
      {label && (
        <Typography
          component="label"
          variant="body2"
          fontWeight={500}
          sx={{
            display: 'block',
            mb: 0.5,
            color: error ? 'error.main' : 'text.primary',
          }}
        >
          {label}
          {required && (
            <Typography component="span" color="error.main" sx={{ ml: 0.5 }}>
              *
            </Typography>
          )}
        </Typography>
      )}

      {/* Editor */}
      <Box sx={editorStyles}>
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          modules={modules}
          formats={formats}
          placeholder={placeholder}
          readOnly={readOnly || disabled}
        />
      </Box>

      {/* Footer with helper text and character count */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          mt: 0.5,
        }}
      >
        {/* Helper Text / Error Message */}
        <FormHelperText error={error} sx={{ m: 0, flex: 1 }}>
          {helperText}
        </FormHelperText>

        {/* Character Count */}
        {showCharCount && (
          <Typography
            variant="caption"
            color={isOverLimit ? 'error' : 'text.secondary'}
            sx={{ ml: 2, whiteSpace: 'nowrap' }}
          >
            {charCount}
            {maxLength && ` / ${maxLength}`}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

export default RichTextEditor;

